import { Modal, Button, Stack, Text, Group, Checkbox, Flex } from '@mantine/core';
import { BsExclamationTriangle } from 'react-icons/bs';
import { useState, useEffect } from 'react';

interface DuplicateFile {
  fileName: string;
  newFile: File;
  fileSize?: number;
  lastModified?: number;
}

interface DuplicateFilesModalProps {
  isOpen: boolean;
  duplicates?: DuplicateFile[];
  onClose: () => void;
  onProceed: (files: File[]) => void;
  isLoading?: boolean;
}

export function DuplicateFilesModal({
  isOpen,
  duplicates = [],
  onClose,
  onProceed,
  isLoading = false
}: DuplicateFilesModalProps) {
  const [selectedFiles, setSelectedFiles] = useState(new Set(duplicates.map((_, i) => i)));

  // Update selected files when duplicates change to ensure all are selected by default
  useEffect(() => {
    setSelectedFiles(new Set(duplicates.map((_, i) => i)));
  }, [duplicates]);

  const handleToggleFile = (index: number) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedFiles(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === duplicates.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(duplicates.map((_, i) => i)));
    }
  };

  const handleProceed = () => {
    if (onProceed) {
      const selectedFileObjects = Array.from(selectedFiles).map(idx => duplicates[idx].newFile);
      onProceed(selectedFileObjects);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Group gap="xs">
          <BsExclamationTriangle color="#ff922b" size={20} />
          <Text fw={600}>Duplicate Files Detected</Text>
        </Group>
      }
      centered
      size="md"
    >
      <Stack gap="md">
        <Text color="dimmed" size="sm">
          The following files already exist in the system. Select which files you would like to
          upload.
        </Text>

        <Flex align="center" gap="12px" mb="8px">
          <Checkbox
            checked={selectedFiles.size === duplicates.length && duplicates.length > 0}
            indeterminate={selectedFiles.size > 0 && selectedFiles.size < duplicates.length}
            onChange={handleSelectAll}
          />
          <Text size="sm" fw={600}>
            {selectedFiles.size === 0
              ? 'Select files to proceed'
              : selectedFiles.size === duplicates.length
                ? 'All files selected'
                : `${selectedFiles.size} of ${duplicates.length} selected`}
          </Text>
        </Flex>

        <div
          style={{
            maxHeight: duplicates.length > 5 ? '320px' : '200px',
            overflowY: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            padding: '12px'
          }}
        >
          {duplicates.map((duplicate, index) => (
            <Flex
              key={index}
              align="flex-start"
              gap="10px"
              p="8px"
              role="button"
              tabIndex={0}
              aria-pressed={selectedFiles.has(index)}
              //@ts-ignore
              sx={{
                borderRadius: '4px',
                backgroundColor: selectedFiles.has(index) ? '#f0f7ff' : '#fafafa',
                borderLeft: selectedFiles.has(index)
                  ? '3px solid #3b82f6'
                  : '3px solid transparent',
                cursor: 'pointer',
                marginBottom: '6px',
                '&:last-child': {
                  marginBottom: 0
                }
              }}
              onClick={() => handleToggleFile(index)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggleFile(index);
                }
              }}
            >
              <Checkbox
                checked={selectedFiles.has(index)}
                onChange={() => handleToggleFile(index)}
                onClick={e => e.stopPropagation()}
                aria-label={`Select ${duplicate.fileName}`}
                styles={{ input: { cursor: 'pointer' } }}
              />
              <Stack gap="2px" style={{ flex: 1 }}>
                <Text
                  fw={500}
                  size="sm"
                  //@ts-ignore
                  sx={{ wordBreak: 'break-word' }}
                >
                  {duplicate.fileName}
                </Text>
                <Text size="xs" color="dimmed">
                  {(() => {
                    const size = duplicate?.newFile?.size ?? duplicate?.fileSize;
                    return size !== undefined ? <>Size: {formatFileSize(size)} •</> : null;
                  })()}
                  Last Modified: {new Date(duplicate.newFile.lastModified).toLocaleDateString()}
                </Text>
              </Stack>
            </Flex>
          ))}
        </div>
        {/* @ts-ignore */}
        <Group position="right" gap="sm">
          <Button variant="default" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleProceed}
            loading={isLoading}
            color="orange"
            disabled={selectedFiles.size === 0}
          >
            Upload {selectedFiles.size > 0 ? `(${selectedFiles.size})` : 'Anyway'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
